import { useState } from 'react';
import { Link } from 'react-router';
import { Mail01, Lock01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { useTranslation } from 'react-i18next';
import { VersionFooter } from '@/components/base/version-footer/version-footer';
import { useLogin } from '@/hooks/api/use-auth';
import { useToast } from '@/hooks/use-toast';

export const LoginPage = () => {
    const { t } = useTranslation(['pages:auth', 'common']);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const toast = useToast();

    const loginMutation = useLogin();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        try {
            await loginMutation.mutateAsync({
                email,
                password,
            });
            // Il redirect viene gestito automaticamente da useLogin.onSuccess
        } catch (err) {
            // Gestisci errori dal login
            const errorMessage = err instanceof Error ? err.message : (t('login.errorLogin') || 'Errore durante il login');
            setError(errorMessage);
            // Mostra anche toast di errore
            toast.error(err);
        }
    };

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
            <div className="w-full max-w-md">
                {/* Logo */}
                <Link to="/" className="flex justify-center items-center gap-2 mb-8 hover:opacity-80 transition-opacity">
                    <img src="/images/logo-full.svg" alt="Eve Mind" className="h-14 w-auto sm:h-16" />
                </Link>

                {/* Login Form */}
                <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary mb-4">
                    <div className="mb-6 text-center">
                        <h1 className="text-2xl font-semibold text-primary mb-2">
                            {t('login.title')}
                        </h1>
                        <p className="text-sm text-tertiary">
                            {t('login.subtitle')}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <Input
                            type="email"
                            name="email"
                            autoComplete="email"
                            label={t('login.emailLabel')}
                            placeholder={t('login.emailPlaceholder')}
                            value={email}
                            onChange={setEmail}
                            icon={Mail01}
                            isRequired
                            size="md"
                        />

                        <Input
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            label={t('login.passwordLabel')}
                            placeholder={t('login.passwordPlaceholder')}
                            value={password}
                            onChange={setPassword}
                            icon={Lock01}
                            isRequired
                            size="md"
                        />

                        <div className="flex items-center justify-between">
                            <Link
                                to="/auth/reset-password"
                                className="text-sm text-brand-secondary hover:text-brand-secondary_hover"
                            >
                                {t('login.forgotPassword')}
                            </Link>
                        </div>

                        {error && (
                            <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            size="lg"
                            color="primary"
                            isLoading={loginMutation.isPending}
                            className="w-full"
                        >
                            {t('login.submitButton')}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-tertiary">
                            {t('login.noAccount')}{' '}
                            <Link
                                to="/auth/register"
                                className="text-brand-secondary hover:text-brand-secondary_hover"
                            >
                                {t('login.contactAdmin')}
                            </Link>
                        </p>
                    </div>
                </div>

                <VersionFooter />
            </div>
        </div>
    );
};
